import { useState, type MouseEvent } from 'react'
import { AppBar, Toolbar, Typography, Box, Button, Menu, MenuItem } from '@mui/material'
import type { AppBarProps } from '@mui/material'
import { LogOut } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/features/auth'

interface TopBarProps {
    position?: AppBarProps['position']
    title?: string
}

const TopBar = ({ position = 'static', title = 'powered by Emergo' }: TopBarProps) => {
    const { t, i18n } = useTranslation()
    const { user, logout } = useAuthStore()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [langAnchorEl, setLangAnchorEl] = useState<HTMLElement | null>(null)

    const handleProfileOpen = (e: MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget)
    const handleProfileClose = () => setAnchorEl(null)
    const handleLangOpen = (e: MouseEvent<HTMLButtonElement>) => setLangAnchorEl(e.currentTarget)
    const handleLangClose = () => setLangAnchorEl(null)

    const handleLogout = () => { logout(); handleProfileClose() }
    const changeLanguage = (lng: string) => { void i18n.changeLanguage(lng); handleLangClose() }

    return (
        <AppBar
            position={position}
            sx={{
                bgcolor: 'background.paper',
                color: 'text.primary',
                boxShadow: 'none',
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            }}
        >
            <Toolbar sx={{ justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 300 }}>{title}</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button onClick={handleLangOpen} sx={{ width: '100px', padding: '10px' }}>
                        {t('language')}
                    </Button>
                    <Button variant="outlined" onClick={handleProfileOpen} sx={{ width: '100px', padding: '10px' }}>
                        {t('user')}
                    </Button>
                </Box>
            </Toolbar>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleProfileClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.12)' } } }}
            >
                <MenuItem disabled>
                    <Typography variant="body2">{user?.name || user?.email || 'User'}</Typography>
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                    <LogOut size={16} style={{ marginRight: 8 }} />
                    {t('logout')}
                </MenuItem>
            </Menu>

            <Menu
                anchorEl={langAnchorEl}
                open={Boolean(langAnchorEl)}
                onClose={handleLangClose}
                slotProps={{
                    list: { sx: { width: '100px' } },
                    paper: { sx: { boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.12)' } },
                }}
            >
                <MenuItem onClick={() => changeLanguage('en')}>English</MenuItem>
                <MenuItem onClick={() => changeLanguage('de')}>Deutsch</MenuItem>
            </Menu>
        </AppBar>
    )
}

export default TopBar
